export const TRAIL_CHARACTER_TAGS = {
  ski: [
    "beautiful",
    "underrated",
    "great-for-carving",
    "quiet-early",
    "fast-finish",
    "exposed",
    "flat-light-caution",
    "awkward-flat-section",
  ],
  mtb: [
    "beautiful",
    "underrated",
    "flowy",
    "technical",
    "quiet-early",
    "fast-finish",
    "exposed",
    "awkward-climb-or-flat",
  ],
} as const;

export const TRAIL_CHARACTER_TAG_LABELS = {
  beautiful: "Beautiful",
  underrated: "Underrated",
  "great-for-carving": "Great for carving",
  "quiet-early": "Quiet early",
  "fast-finish": "Fast finish",
  exposed: "Exposed",
  "flat-light-caution": "Flat-light caution",
  "awkward-flat-section": "Awkward flat section",
  flowy: "Flowy",
  technical: "Technical",
  "awkward-climb-or-flat": "Awkward climb or flat",
} as const;

export const LIFT_CHARACTER_TAGS = [
  "scenic-lift",
  "queues-build-quickly",
  "quick-mountain-link",
  "weather-exposed",
] as const;

export const LIFT_CHARACTER_TAG_LABELS = {
  "scenic-lift": "Scenic lift",
  "queues-build-quickly": "Queues build quickly",
  "quick-mountain-link": "Quick mountain link",
  "weather-exposed": "Weather exposed",
} as const;

export const RECENT_CONDITION_TAGS = {
  ski: ["great-snow-today", "icy", "busy", "thin-cover"],
  mtb: ["tacky", "dusty", "muddy", "busy"],
} as const;

export const RECENT_CONDITION_TAG_LABELS = {
  "great-snow-today": "Great snow today",
  icy: "Icy",
  busy: "Busy",
  "thin-cover": "Thin cover",
  tacky: "Tacky",
  dusty: "Dusty",
  muddy: "Muddy",
} as const;

export const PERSONAL_RUN_VOTES = ["favourite", "worst"] as const;

export const PERSONAL_RUN_VOTE_LABELS = {
  favourite: "Favourite",
  worst: "Worst",
} as const;

export const COMMENT_MAX_LENGTH = 280;
export const PUBLIC_TAG_THRESHOLD = 3;
export const PUBLIC_VERDICT_THRESHOLD = 5;
export const MOUNTAIN_AREA_SLUG_MAX_LENGTH = 100;

const DISPLAY_NAME_MIN_LENGTH = 2;
const DISPLAY_NAME_MAX_LENGTH = 40;
const MAX_ENDURING_TAGS = 5;

export type MountainSport = "ski" | "mtb";
export type MountainFeatureType = "trail" | "lift";

export type MountainFeatureRef = {
  areaSlug: string;
  sport: MountainSport;
  featureType: MountainFeatureType;
  featureId: string;
};

export type MountainInsightInput = MountainFeatureRef & {
  tags: string[];
  displayName: string;
  comment: string | null;
  trackedSessionId: string | null;
};

export type MountainInsightValidationError = {
  field: keyof MountainInsightInput | "input";
  message: string;
};

export type MountainInsightValidationResult =
  | { ok: true; value: MountainInsightInput }
  | { ok: false; errors: MountainInsightValidationError[] };

export type PublicMountainCommunity = {
  tags: Array<{ key: string; label: string; count: number }>;
  verdicts: {
    favouriteCount: number | null;
    worstCount: number | null;
  };
  comments: Array<{
    id: string;
    displayName: string;
    body: string;
    trackedOnPeakWave: boolean;
    createdAt: string;
  }>;
};

export type MountainCommunityTagVoteRow = {
  userId: string;
  tagKey: string;
  suspended?: boolean;
  removed?: boolean;
  [privateField: string]: unknown;
};

export type MountainCommunityVerdictVoteRow = {
  userId: string;
  voteType: (typeof PERSONAL_RUN_VOTES)[number];
  suspended?: boolean;
  removed?: boolean;
  [privateField: string]: unknown;
};

export type MountainCommunityCommentRow = {
  id: string;
  userId: string;
  displayName: string;
  body: string;
  trackedEvidenceVerified: boolean;
  createdAt: string;
  status: "pending" | "approved" | "rejected" | "hidden";
  suspended?: boolean;
  removed?: boolean;
  [privateField: string]: unknown;
};

export type MountainCommunityAggregateInput = {
  tagVotes: MountainCommunityTagVoteRow[];
  verdictVotes: MountainCommunityVerdictVoteRow[];
  comments: MountainCommunityCommentRow[];
  view?: "compact" | "expanded";
  [ignoredField: string]: unknown;
};

const ALL_ENDURING_TAG_LABELS: Readonly<Record<string, string>> = {
  ...TRAIL_CHARACTER_TAG_LABELS,
  ...LIFT_CHARACTER_TAG_LABELS,
};

const CONTROL_CHARACTER = /[\p{Cc}\p{Cf}]/u;
const ANGLE_BRACKET = /[<>]/;
const DOMAIN_SEPARATOR = "[.\\u3002\\uFF0E\\uFF61]";
const DOMAIN_LABEL = "[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]{0,61}[\\p{L}\\p{N}])?";
const DOMAIN_SUFFIX = "(?:xn--[a-z0-9-]{2,59}|[\\p{L}][\\p{L}\\p{N}]{1,62})";
const URL = new RegExp(
  `(?:(?:https?|ftp):\\/\\/|www\\.|(?:${DOMAIN_LABEL}${DOMAIN_SEPARATOR})+${DOMAIN_SUFFIX}(?=$|[\\/?#:\\s]|\\p{P}))`,
  "iu",
);
const LOCALHOST = /\blocalhost(?::\d{1,5})?(?:[/?#][^\s]*)?(?=$|[\s,;.!?])/iu;
const BRACKETED_IP_HOST = /^\[([0-9a-f:.]+)\](?::\d{1,5})?(?:[/?#].*)?$/iu;
const IPV4_HOST = /^(\d{1,3}(?:\.\d{1,3}){3})(?::\d{1,5})?(?:[/?#].*)?$/u;
const MARKDOWN = /(?:\*\*|__|~~|`|!\[|\[[^\]]+\]\([^)]+\)|!?\[[^\]]+\]\[[^\]]*\]|\*[^*]+\*|_[^_]+_|(?:^|\s)#{1,6}\s|^(?:[-+*]\s+|\d+[.)]\s+)|^(?:(?:\*\s*){3,}|(?:-\s*){3,}|(?:_\s*){3,})$)/;

export function normaliseMountainInsightInput(
  input: unknown,
): MountainInsightValidationResult {
  if (!isRecord(input)) {
    return validationFailure("input", "Insight input must be an object");
  }

  const errors: MountainInsightValidationError[] = [];
  const areaSlug = readRequiredString(input.areaSlug, "areaSlug", errors);
  const featureId = readRequiredString(input.featureId, "featureId", errors);
  const sport = input.sport === "ski" || input.sport === "mtb" ? input.sport : null;
  const featureType =
    input.featureType === "trail" || input.featureType === "lift"
      ? input.featureType
      : null;

  if (sport === null) {
    errors.push({ field: "sport", message: "Sport must be ski or mtb" });
  }
  if (featureType === null) {
    errors.push({ field: "featureType", message: "Feature type must be trail or lift" });
  }

  const displayName = normaliseText(input.displayName, "displayName", errors);
  if (
    displayName === null &&
    typeof input.displayName === "string" &&
    input.displayName.trim() === ""
  ) {
    errors.push({ field: "displayName", message: "Display name is required" });
  }
  if (
    displayName !== null &&
    (characterLength(displayName) < DISPLAY_NAME_MIN_LENGTH ||
      characterLength(displayName) > DISPLAY_NAME_MAX_LENGTH)
  ) {
    errors.push({
      field: "displayName",
      message: "Display name must be between 2 and 40 characters",
    });
  }

  let comment: string | null = null;
  if (input.comment !== null) {
    comment = normaliseText(input.comment, "comment", errors);
    if (comment !== null && characterLength(comment) > COMMENT_MAX_LENGTH) {
      errors.push({
        field: "comment",
        message: `Comment must be ${COMMENT_MAX_LENGTH} characters or fewer`,
      });
    }
  }

  const tags = Array.isArray(input.tags) && input.tags.every((tag) => typeof tag === "string")
    ? input.tags
    : null;
  if (tags === null) {
    errors.push({ field: "tags", message: "Tags must be an array of strings" });
  } else {
    if (new Set(tags).size !== tags.length) {
      errors.push({ field: "tags", message: "Tags must not contain duplicates" });
    }
    if (tags.length > MAX_ENDURING_TAGS) {
      errors.push({ field: "tags", message: "Choose no more than five enduring tags" });
    }
    if (sport !== null && featureType !== null) {
      const allowedTags: readonly string[] =
        featureType === "lift" ? LIFT_CHARACTER_TAGS : TRAIL_CHARACTER_TAGS[sport];
      if (tags.some((tag) => !allowedTags.includes(tag))) {
        errors.push({ field: "tags", message: "One or more tags do not apply to this feature" });
      }
    }
    if (tags.length === 0 && !comment) {
      errors.push({ field: "tags", message: "Add at least one tag or a comment" });
    }
  }

  const trackedSessionId =
    input.trackedSessionId === null || typeof input.trackedSessionId === "string"
      ? input.trackedSessionId
      : null;
  if (input.trackedSessionId !== null && typeof input.trackedSessionId !== "string") {
    errors.push({
      field: "trackedSessionId",
      message: "Tracked session id must be a string or null",
    });
  }

  if (
    errors.length > 0 ||
    areaSlug === null ||
    featureId === null ||
    sport === null ||
    featureType === null ||
    displayName === null ||
    tags === null
  ) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    value: {
      areaSlug,
      sport,
      featureType,
      featureId,
      tags: [...tags],
      displayName,
      comment,
      trackedSessionId,
    },
  };
}

export function buildPublicMountainCommunity(
  input: MountainCommunityAggregateInput,
): PublicMountainCommunity {
  const tagMembers = new Map<string, Set<string>>();
  for (const row of input.tagVotes) {
    if (!isEligible(row) || ALL_ENDURING_TAG_LABELS[row.tagKey] === undefined) continue;
    const members = tagMembers.get(row.tagKey) ?? new Set<string>();
    members.add(row.userId);
    tagMembers.set(row.tagKey, members);
  }

  const tags = [...tagMembers]
    .filter(([, members]) => members.size >= PUBLIC_TAG_THRESHOLD)
    .map(([key, members]) => ({
      key,
      label: ALL_ENDURING_TAG_LABELS[key],
      count: members.size,
    }))
    .sort((left, right) => right.count - left.count || compareText(left.label, right.label));

  const visibleTags = input.view === "expanded" ? tags : tags.slice(0, 3);
  const verdictMembers = {
    favourite: new Set<string>(),
    worst: new Set<string>(),
  };
  for (const row of input.verdictVotes) {
    if (!isEligible(row) || !(row.voteType in verdictMembers)) continue;
    verdictMembers[row.voteType].add(row.userId);
  }

  const comments = input.comments
    .filter((row) => isEligible(row) && row.status === "approved")
    .sort(
      (left, right) =>
        compareText(right.createdAt, left.createdAt) || compareText(left.id, right.id),
    )
    .map((row) => ({
      id: row.id,
      displayName: row.displayName,
      body: row.body,
      trackedOnPeakWave: row.trackedEvidenceVerified === true,
      createdAt: row.createdAt,
    }));

  return {
    tags: visibleTags,
    verdicts: {
      favouriteCount: publicCount(verdictMembers.favourite.size, PUBLIC_VERDICT_THRESHOLD),
      worstCount: publicCount(verdictMembers.worst.size, PUBLIC_VERDICT_THRESHOLD),
    },
    comments,
  };
}

function normaliseText(
  value: unknown,
  field: "displayName" | "comment",
  errors: MountainInsightValidationError[],
): string | null {
  if (typeof value !== "string") {
    errors.push({ field, message: `${field === "displayName" ? "Display name" : "Comment"} must be text` });
    return null;
  }
  if (CONTROL_CHARACTER.test(value)) {
    errors.push({ field, message: "Control characters are not allowed" });
    return null;
  }

  const normalised = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
  if (CONTROL_CHARACTER.test(normalised)) {
    errors.push({ field, message: "Control characters are not allowed" });
    return null;
  }
  if (normalised === "") return null;
  if (
    ANGLE_BRACKET.test(normalised) ||
    URL.test(normalised) ||
    LOCALHOST.test(normalised) ||
    containsIpHostLiteral(normalised) ||
    MARKDOWN.test(normalised)
  ) {
    errors.push({ field, message: "Links and markup are not allowed" });
    return null;
  }
  return normalised;
}

function readRequiredString(
  value: unknown,
  field: "areaSlug" | "featureId",
  errors: MountainInsightValidationError[],
): string | null {
  if (typeof value !== "string" || value.trim() === "") {
    errors.push({ field, message: `${field === "areaSlug" ? "Area slug" : "Feature id"} is required` });
    return null;
  }
  return value.trim();
}

function validationFailure(
  field: MountainInsightValidationError["field"],
  message: string,
): MountainInsightValidationResult {
  return { ok: false, errors: [{ field, message }] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function characterLength(value: string): number {
  return [...value].length;
}

function containsIpHostLiteral(value: string): boolean {
  return value.split(/\s+/u).some((rawToken) => {
    const token = rawToken
      .replace(/^[('"“‘]+/u, "")
      .replace(/[),.;!?"'”’]+$/u, "");
    const bracketed = token.match(BRACKETED_IP_HOST);
    if (bracketed) return isValidIpv6(bracketed[1]);

    const ipv4 = token.match(IPV4_HOST);
    if (ipv4) return isValidIpv4(ipv4[1]);

    const pathStart = token.search(/[/?#]/u);
    const possibleIpv6 = pathStart === -1 ? token : token.slice(0, pathStart);
    return isValidIpv6(possibleIpv6);
  });
}

function isValidIpv4(value: string): boolean {
  const octets = value.split(".");
  return (
    octets.length === 4 &&
    octets.every((octet) => /^\d{1,3}$/u.test(octet) && Number(octet) <= 255)
  );
}

function isValidIpv6(value: string): boolean {
  if (!value.includes(":")) return false;
  const compressedParts = value.split("::");
  if (compressedParts.length > 2) return false;

  const left = ipv6GroupCount(compressedParts[0], compressedParts.length === 1);
  const right =
    compressedParts.length === 2 ? ipv6GroupCount(compressedParts[1], true) : 0;
  if (left === null || right === null) return false;

  const groupCount = left + right;
  return compressedParts.length === 2 ? groupCount < 8 : groupCount === 8;
}

function ipv6GroupCount(value: string, mayContainIpv4: boolean): number | null {
  if (value === "") return 0;
  const groups = value.split(":");
  let count = groups.length;

  for (const [index, group] of groups.entries()) {
    if (group.includes(".")) {
      if (!mayContainIpv4 || index !== groups.length - 1 || !isValidIpv4(group)) {
        return null;
      }
      count += 1;
    } else if (!/^[0-9a-f]{1,4}$/iu.test(group)) {
      return null;
    }
  }

  return count;
}

function isEligible(row: { suspended?: boolean; removed?: boolean }): boolean {
  return row.suspended !== true && row.removed !== true;
}

function publicCount(count: number, threshold: number): number | null {
  return count >= threshold ? count : null;
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
